import { Button, Form, InputGroup } from 'react-bootstrap';
import {
    forwardRef,
    useRef,
    type FocusEvent,
    type KeyboardEvent,
    type ReactNode,
} from 'react';
import SolarIcon from 'components/icons/SolarIcon';

export interface InputQuantityProps {
    label: string;
    value: number;
    onDown: () => void;
    onUp: () => void;
    onChange: (value: string) => void;
    onEnter?: () => void;
    min?: number;
    step?: number;
    disableDownAt?: number;
    className?: string;
    inputGroupClassName?: string;
    buttonClassName?: string;
    inputClassName?: string;
    decreaseAriaLabel?: string;
    increaseAriaLabel?: string;
    actions?: ReactNode;
    actionsClassName?: string;
}

const InputQuantity = forwardRef<HTMLInputElement, InputQuantityProps>(function InputQuantity(
    {
        label,
        value,
        onDown,
        onUp,
        onChange,
        onEnter,
        min = 1,
        step = 1,
        disableDownAt = 1,
        className = 'mb-0',
        inputGroupClassName = 'proposal-qty-group flex-shrink-0',
        buttonClassName,
        inputClassName = 'text-center',
        decreaseAriaLabel,
        increaseAriaLabel,
        actions,
        actionsClassName = 'd-flex gap-1 align-items-stretch',
    },
    ref,
) {
    const selectOnMouseUpRef = useRef(false);

    const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
        selectOnMouseUpRef.current = true;
        e.target.select();
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key !== 'Enter' || !onEnter) return;
        e.preventDefault();
        e.stopPropagation();
        onEnter();
    };

    const inputGroup = (
        <InputGroup size="sm" className={inputGroupClassName}>
            <Button
                type="button"
                variant="outline-secondary"
                className={buttonClassName}
                disabled={value <= disableDownAt}
                onClick={onDown}
                aria-label={decreaseAriaLabel}
            >
                <SolarIcon name="dash" />
            </Button>
            <Form.Control
                ref={ref}
                type="text"
                inputMode="decimal"
                min={min}
                step={step}
                className={inputClassName}
                value={value}
                onFocus={handleFocus}
                onMouseUp={(e) => {
                    if (!selectOnMouseUpRef.current) return;
                    e.preventDefault();
                    selectOnMouseUpRef.current = false;
                    e.currentTarget.select();
                }}
                onChange={(e) => onChange(e.target.value)}
                onKeyDown={handleKeyDown}
            />
            <Button
                type="button"
                variant="outline-secondary"
                className={buttonClassName}
                onClick={onUp}
                aria-label={increaseAriaLabel}
            >
                <SolarIcon name="plus" />
            </Button>
        </InputGroup>
    );

    return (
        <Form.Group className={className}>
            <Form.Label className="text-muted mb-1 fs-12">{label}</Form.Label>
            {actions ? (
                <div className={actionsClassName}>
                    {inputGroup}
                    {actions}
                </div>
            ) : (
                inputGroup
            )}
        </Form.Group>
    );
});

export default InputQuantity;
